import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  divide as _divide,
  filter as _filter,
  get as _get,
  round as _round,
  uniq as _uniq
} from 'lodash';
import { combineLatest, Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';

import { PolicySelectors } from '@amfam/policy/data-access';
import { PolicyUtilService } from '@amfam/policy/e-signature/data-access';
import {
  ANSRequestModel,
  AutoPolicy,
  CustomerEmailRequest,
  MessageModelNoQuote,
  MessageRequestNoQuote,
  NotificationRequest,
  NotificationRequestCustomerEmail,
  Policy,
  ReplaceVehicleRequestCustomerEmail,
  RiskModel
} from '@amfam/policy/models';
import { vehicleQuery } from '@amfam/policy/vehicle/data-access';
import { userQuery } from '@amfam/shared/user';
import { Agent, agentQuery, fromAgentActions, initialAgent } from '@amfam/shared/utility/agent';
import { BrandSelectors } from '@amfam/shared/utility/brand';
import { ImpersonateRolesService } from '@amfam/shared/utility/impersonation';
import { CopyService } from '@amfam/shared/utility/shared-services';
import { DockingBarService, ErrorLevels, ErrorObj, PageTitleService } from '@amfam/ui-kit';

import {
  AnalyticsEventType,
  ReplaceVehicleQuickQuoteOperation
} from '../+state/quick-quote-feature/quick-quote-enum';
import { QuickQuoteFeatureFacade } from '../+state/quick-quote-feature/quick-quote-feature.facade';
import { QuickQuoteFeatureService } from '../services/quick-quote-feature.service';

@Component({
  selector: 'ds-online-bind-failure',
  templateUrl: './online-bind-failure.component.html',
  styleUrls: ['./online-bind-failure.component.scss'],
  standalone: false
})
export class OnlineBindFailureComponent implements OnInit, AfterViewInit, OnDestroy {
  premiumDetails: any = {
    quoteExpirationDate: new Date(),
    current: {},
    new: {}
  };

  private vehicleId;
  private policyList: AutoPolicy[];
  private partnerId: string;
  private customerId: string;
  private customerEmail: string;
  private customerName: string;

  private selectedPolicy: Policy;
  private selectedProducer: Agent;

  public newVehicle: string;
  public newVehicleVin: string;
  public currentVehicle = 'current vehicle';
  private matchingvehicle: RiskModel;

  errorMessage: string;
  public errorList: Array<ErrorObj> = [];

  private isUsedForBusiness: NotificationRequest['isUsedForBusiness'];
  private isOwner: NotificationRequest['isOwner'];
  private hasTPI: NotificationRequest['hasTPI'];
  private existingTPI: NotificationRequest['existingTPI'];
  private copyTPIIndicator: NotificationRequest['copyTPIIndicator'];

  // Shared subject for completing observables
  private stop$: Subject<void> = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private pageTitle: PageTitleService,
    private dockingBarService: DockingBarService,
    private store: Store<any>,
    private policyUtilService: PolicyUtilService,
    public roleService: ImpersonateRolesService,
    private copyService: CopyService,
    private quickQuoteFeatureFacade: QuickQuoteFeatureFacade,
    private quickQuoteFeatureService: QuickQuoteFeatureService
  ) {}

  ngOnInit() {
    this.pageTitle.set('Replace a Vehicle');
    this.dockingBarService.registerHeading('Replace a Vehicle');

    combineLatest(
      this.route.params,
      this.store.select(PolicySelectors.selectAdvanceAutoPolicies),
      this.store.select(PolicySelectors.selectAdvanceAutoPolicyRisks),
      this.store.select(vehicleQuery.selectNewVehicle),
      this.store.select(vehicleQuery.selectCriteria),
      this.store.select(agentQuery.getAgentEntities),
      (params, policies, risks, newVehicle, vehicleSelectionDetails, agents) => {
        return {
          vehicleId: params['id'],
          policies: policies,
          vehicles: risks,
          newVehicle: newVehicle,
          vehicleSelectionDetails: vehicleSelectionDetails,
          agents: agents
        };
      }
    )
      .pipe(takeUntil(this.stop$))
      .subscribe(state => {
        this.vehicleId = state.vehicleId;
        this.policyList = state.policies;

        let matchingPolicy;

        const matchingVehicle = state.vehicles.find(vehicle => {
          return _get(vehicle, 'id') === this.vehicleId;
        });

        if (matchingVehicle) {
          const policyNumber = matchingVehicle.policyNumber;
          this.matchingvehicle = matchingVehicle;

          matchingPolicy = state.policies.find(policy => {
            return _get(policy, 'policyNumber') === policyNumber;
          });

          if (matchingPolicy) {
            const currentTermMonths = Number(matchingPolicy.termDescription.split(' ')[0]);
            const monthlyCost = _round(_divide(matchingPolicy.totalPremium, currentTermMonths), 2);
            this.premiumDetails.current.description = 'Current';
            this.premiumDetails.current.fullCost = Number(
              _get(matchingPolicy, 'totalPremium', 0)
            ).toFixed(2);
            this.premiumDetails.current.policyTermMonths = currentTermMonths + ' months';
            this.premiumDetails.current.monthlyCost = monthlyCost.toFixed(2);

            this.premiumDetails.new.description = 'Quote';
            this.premiumDetails.new.newVehicle = state.newVehicle;

            if (
              this.matchingvehicle &&
              this.matchingvehicle.year &&
              this.matchingvehicle.make &&
              this.matchingvehicle.model
            ) {
              this.currentVehicle =
                this.matchingvehicle.year +
                ' ' +
                this.matchingvehicle.make +
                ' ' +
                this.matchingvehicle.model;
            }

            this.newVehicle =
              _get(state, 'newVehicle.year') +
              ' ' +
              _get(state, 'newVehicle.make', '') +
              ' ' +
              _get(state, 'newVehicle.model', '');
            this.newVehicleVin = state.newVehicle.vin;
            this.isUsedForBusiness = this.policyUtilService.getUnderWritingQuestionsDetails(
              _get(state, 'vehicleSelectionDetails'),
              'policy.underWritingQuestionIsBusinessUse',
              true
            );

            this.isOwner = this.policyUtilService.getUnderWritingQuestionsDetails(
              _get(state, 'vehicleSelectionDetails'),
              'policy.underWritingQuestionIsOwner',
              true
            );

            this.hasTPI = this.policyUtilService.getUnderWritingQuestionsDetails(
              _get(state, 'vehicleSelectionDetails'),
              'policy.underWritingQuestionHasLoan',
              false
            );

            this.existingTPI = _get(state, 'vehicleSelectionDetails.TPIName', '');

            this.copyTPIIndicator =
              _get(state, 'vehicleSelectionDetails.copyTPIIndicator', '') === 'yes' ? 'Yes' : 'No';

            this.selectedPolicy = matchingPolicy;
            // JP: Check if we've loaded producer already - dispatch load if not
            this.selectedProducer =
              state.agents[_get(this.selectedPolicy, 'assignedProducer.producerIdNum')];
            if (!this.selectedProducer) {
              this.store.dispatch(
                new fromAgentActions.LoadAgentAction(
                  Object.assign({}, initialAgent, {
                    id: _get(this.selectedPolicy, 'assignedProducer.producerIdNum')
                  })
                )
              );
            }
          }
        }
      });

    // JP: After Load action has been dispatched, subscribe again and fetch the correct producer
    if (!this.selectedProducer) {
      combineLatest(
        this.store.select(agentQuery.getAreAllAgentsLoaded),
        this.store.select(agentQuery.getAgentEntities),
        (loaded, agents) => {
          return {
            loaded: loaded,
            agents: agents
          };
        }
      )
        .pipe(
          filter(data => data.loaded),
          takeUntil(this.stop$)
        )
        .subscribe(data => {
          this.selectedProducer =
            data.agents[_get(this.selectedPolicy, 'assignedProducer.producerIdNum')];
        });
    }

    combineLatest(
      this.store.select(userQuery.selectUserState),
      this.store.select(BrandSelectors.selectPartnerId),
      this.store.select(BrandSelectors.selectIsPartner),
      (user, partner, isPartner) => {
        return {
          user: user,
          partner: partner,
          isPartner: isPartner
        };
      }
    ).subscribe(res => {
      this.partnerId = res.partner;
      this.customerName = _get(res, 'user.firstName', '') + ' ' + _get(res, 'user.lastName', '');
      this.customerId = _get(res, 'user.customerId', '');
      this.customerEmail = _get(res, 'user.emailAddress', '');
    });
  }

  ngAfterViewInit() {
    this.quickQuoteFeatureFacade.sendAnalytics(AnalyticsEventType.REPLACE_VEHICLE_HARD_STOP);
  }

  submit() {
    this.quickQuoteFeatureFacade.setOperation(
      ReplaceVehicleQuickQuoteOperation.REPLACE_VEHICLE_SEND_AGENT
    );

    const customerNotificationRequest: NotificationRequestCustomerEmail = {
      email: new ReplaceVehicleRequestCustomerEmail(this.getCustomerEmailRequestModel()),
      message: new MessageModelNoQuote(this.getMessageModel()),
      templateId: 'ReplaceVehicleAgentFollowup'
    };

    const customerName = this.customerName;
    const policyNumber = _get(this.selectedPolicy, 'policyNumber', '');
    const newVehicle = customerNotificationRequest.message.newRiskInformation;
    const oldVehicle = customerNotificationRequest.message.riskInformation;
    const existingVehicleVin = customerNotificationRequest.message.existingVehicleVin;
    const newVehicleVin = customerNotificationRequest.message.newVehicleVin;

    const agentNotificationRequest: NotificationRequest = {
      producer: this.selectedProducer,
      vehicleId: this.vehicleId,
      customerName: customerName,
      policyNumber: policyNumber,
      newVehicle: newVehicle,
      oldVehicle: oldVehicle,
      email: this.customerEmail,
      type: 'No Quote',
      isUsedForBusiness: this.isUsedForBusiness,
      copyTPIIndicator: this.copyTPIIndicator,
      isOwner: this.isOwner,
      hasTPI: this.hasTPI,
      existingTPI: this.existingTPI,
      existingVehicleVin: existingVehicleVin,
      newVehicleVin: newVehicleVin
    };

    const existingTPI =
      this.copyTPIIndicator === 'Yes'
        ? _get(this, 'existingTPI', '')
        : this.copyService.getCopy('policy.policyChangeAgentFollowUpText');

    const notificationMsg = this.copyService.getCopy('policy.ANSContentQuoteToAgent', {
      customerName: customerName,
      policyNumber: policyNumber,
      newVehicle: newVehicle,
      oldVehicle: oldVehicle,
      email: this.customerEmail,
      isUsedForBusiness: this.isUsedForBusiness,
      isOwner: this.isOwner,
      hasTPI: this.hasTPI,
      existingTPI: this.hasTPI === 'Yes' ? existingTPI : 'Not Applicable',
      orderNumber: 'NA'
    });

    const ansRequest: ANSRequestModel = new ANSRequestModel({
      producer: this.selectedProducer,
      orderNumber: 'NA',
      policyNumber: _get(this.selectedPolicy, 'policyNumber'),
      from: this.copyService.getCopy('shared.myAccountTxt'),
      customerName: this.customerName,
      notificationText: notificationMsg,
      vehicleId: this.vehicleId
    });

    // Dispatch an action to notify the agents and customers via email.

    this.quickQuoteFeatureService
      .buildANSNotificationPayload(
        this.selectedProducer.agentId,
        this.selectedPolicy.policyNumber,
        ansRequest.notificationText,
        'NA'
      )
      .pipe(take(1))
      .subscribe(payload => {
        this.quickQuoteFeatureFacade.replaceVehicleANSNotification(payload);
      });

    this.quickQuoteFeatureFacade.replaceVehicleNotifyAgent(
      this.quickQuoteFeatureService.buildInternalEmail(agentNotificationRequest)
    );

    this.quickQuoteFeatureFacade.replaceVehicleNotifyCustomer(
      this.quickQuoteFeatureService.buildCustomerEmail(
        customerNotificationRequest.email,
        this.quickQuoteFeatureService.buildMessageDetails(customerNotificationRequest.message),
        customerNotificationRequest.templateId
      )
    );

    this.quickQuoteFeatureFacade.sendAnalytics(
      AnalyticsEventType.REPLACE_VEHICLE_HARD_STOP_SEND_TO_AGENT
    );
  }

  private getCustomerEmailRequestModel(): CustomerEmailRequest {
    const customerEmailRequest: CustomerEmailRequest = {
      customerEmailId: this.customerEmail,
      customerId: this.customerId,
      partnerId: this.partnerId
    };
    return customerEmailRequest;
  }

  private getMessageModel(): MessageRequestNoQuote {
    let drivers: string;
    let coverages: string;
    let discounts: string;
    let fees: string;

    for (const vehicleNamedInsured of _uniq(
      _filter(this.matchingvehicle.vehicleRoles, { roleType: 'PrimaryDriver' })
    )) {
      if (drivers) {
        drivers += ', ' + _get(vehicleNamedInsured, 'name');
      } else {
        drivers = _get(vehicleNamedInsured, 'name');
      }
    }
    for (const vehicleCoverage of _uniq(_get(this.matchingvehicle, 'vehicleCoverages', []))) {
      if (coverages) {
        coverages += ', ' + _get(vehicleCoverage, 'name');
      } else {
        coverages = _get(vehicleCoverage, 'name');
      }
    }
    // Pull vehicle-level discounts
    for (const vehicleDiscount of _uniq(_get(this.matchingvehicle, 'vehicleDiscounts', []))) {
      if (discounts) {
        discounts += ', ' + _get(vehicleDiscount, 'name');
      } else {
        discounts = _get(vehicleDiscount, 'name');
      }
    }
    // Pull policy-level discounts
    for (const policyDiscount of _uniq(_get(this.matchingvehicle, 'policy.policyDiscounts', []))) {
      if (discounts) {
        discounts += ', ' + _get(policyDiscount, 'name');
      } else {
        discounts = _get(policyDiscount, 'name');
      }
    }
    // Pull vehicle-level fees
    for (const vehicleFee of _uniq(_get(this.matchingvehicle, 'vehicleFees', []))) {
      if (fees) {
        fees += ', ' + _get(vehicleFee, 'name');
      } else {
        fees = _get(vehicleFee, 'name');
      }
    }
    // Pull policy-level fees
    for (const policyFee of _uniq(_get(this.matchingvehicle, 'policy.policyFees', []))) {
      if (fees) {
        fees += ', ' + _get(policyFee, 'name');
      } else {
        fees = _get(policyFee, 'name');
      }
    }
    for (const vehicleSurchanges of _uniq(
      _get(this.matchingvehicle, 'policy.policySurcharges', [])
    )) {
      if (fees) {
        fees += ', ' + _get(vehicleSurchanges, 'name');
      } else {
        fees = _get(vehicleSurchanges, 'name');
      }
    }

    const messageModelRequest: MessageRequestNoQuote = {
      firstName: this.customerName,
      policyNumber: this.selectedPolicy.policyNumber,
      riskInformation: this.matchingvehicle.description,
      existingVehicleVin: this.matchingvehicle.vin,
      totalPremium: _get(this.premiumDetails, 'current.fullCost'),
      monthlyPremium: _get(this.premiumDetails, 'current.monthlyCost'),
      termDescription: _get(this.premiumDetails, 'current.policyTermMonths'),
      newRiskInformation: this.newVehicle,
      newVehicleVin: this.newVehicleVin,
      garagedAddress: _get(this.matchingvehicle, 'address.addressLine1'),
      drivers: drivers,
      discounts: discounts,
      fees: fees,
      coverages: coverages,
      producerId: _get(this, 'selectedProducer.producer.id', '')
    };
    return messageModelRequest;
  }

  private showErrorMessage() {
    this.errorList = [];
    this.errorList.push({
      errorMessage: this.copyService.getCopy('policy.ansNotificationFailError'),
      errorLevel: ErrorLevels.HIGH
    });
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
  }
}
